import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, HelpCircle, Eye } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';

const icons = [AlertCircle, HelpCircle, Eye];

const ProblemSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const t = familyTranslations[lang].problemData;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          {/* Left Content */}
          <div
            className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#5B4FE9]">
              {t.label}
            </span>
            <h2
              className="mt-4 text-3xl sm:text-4xl md:text-[44px] font-bold text-gray-900 leading-[1.15] tracking-tight"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {t.title}
            </h2>
            <p className="mt-6 text-lg text-gray-500 leading-relaxed">
              {t.description}
            </p>
            {/* Result callout */}
            <div className="mt-8 p-5 bg-amber-50/70 border border-amber-100 rounded-xl flex items-start gap-3">
              <div className="mt-0.5">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <p className="text-sm text-amber-800">
                <span className="font-semibold">{t.resultLabel}</span> {t.result}
              </p>
            </div>
          </div>

          {/* Right Cards */}
          <div className="space-y-4">
            {t.cards.map((card, i) => {
              const Icon = icons[i];
              return (
                <div
                  key={i}
                  className={`p-6 rounded-2xl border border-gray-100 bg-white hover:shadow-lg hover:border-gray-200 transition-all duration-500 ${
                    isVisible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-6'
                  }`}
                  style={{ transitionDelay: `${200 + i * 150}ms` }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">{card.title}</h3>
                      <p className="mt-1.5 text-sm text-gray-500 leading-relaxed">
                        {card.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
